import { absoluteTime } from '@lib/time';
import { Button } from '@shared/ui';

import { reasonLabel } from '../model/reasons';

import type { RefusalRecord } from '../schemas/refusals';
import type { ReactNode } from 'react';

function Th({ children }: { children: ReactNode }) {
    return (
        <th className="whitespace-nowrap border-b border-line-subtle px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-content-muted">
            {children}
        </th>
    );
}

function Td({ children, mono = false }: { children?: ReactNode; mono?: boolean }) {
    return (
        <td
            className={
                mono
                    ? 'max-w-[160px] truncate border-b border-line-subtle px-4 py-3 font-mono text-[12px]'
                    : 'border-b border-line-subtle px-4 py-3'
            }
        >
            {children ?? '—'}
        </td>
    );
}

/**
 * Newest first, as the server returns it. A row opens the detail; "Load more"
 * appends the next page and is gone once there is no cursor left.
 */
export function RefusalsTable({
    records,
    hasMore,
    loadingMore,
    onLoadMore,
    onOpen,
}: {
    records: readonly RefusalRecord[];
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    onOpen: (record: RefusalRecord) => void;
}) {
    return (
        <>
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]" data-testid="unidentified-refusals-table">
                    <thead>
                        <tr>
                            <Th>Time</Th>
                            <Th>Action</Th>
                            <Th>Reason</Th>
                            <Th>Provider</Th>
                            <Th>Domain</Th>
                            <Th>IP</Th>
                            <Th>Trace ID</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((record) => (
                            <tr
                                key={record.id}
                                tabIndex={0}
                                onClick={() => onOpen(record)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onOpen(record);
                                    }
                                }}
                                className="cursor-pointer text-content-body hover:bg-surface-sunken focus:bg-surface-sunken focus:outline-none"
                                data-testid={`unidentified-refusals-row-${record.id}`}
                            >
                                <Td>{absoluteTime(record.occurredAt)}</Td>
                                <Td>{record.action}</Td>
                                <Td>{reasonLabel(record.reason)}</Td>
                                <Td>{record.provider}</Td>
                                <Td>{record.domain}</Td>
                                <Td mono>{record.ipAddress}</Td>
                                <Td mono>{record.traceId}</Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {hasMore && (
                <div className="flex justify-center px-5 py-4">
                    <Button variant="ghost" onClick={onLoadMore} disabled={loadingMore}>
                        {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                </div>
            )}
        </>
    );
}
